import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, State } from '@stencil/core';
import {
  JuiControlAlign,
  JuiControlNumberFormat,
  JuiControlNumberStyle,
  JuiControlSize,
  JuiValidationEvents
} from '../../../shared/form-controls/jui-form-control.model';
import { NumberParser } from '../../../shared/form-controls/number-parser';
import { IconSize, IconVariant } from '../../jui-icon/jui-icon.model';
import {
  renderTooltipOnBottom,
  renderTooltipOnLeft,
  renderFormError
} from '../../../shared/form-controls/jui-form-control.utils';

@Component({
  tag: 'jui-number-input',
  styleUrl: 'jui-number-input.scss',
  shadow: true
})
export class JuiNumberInput implements ComponentInterface {
  @Element() el: HTMLElement;

  /**
   * The number input format to use, can be 'integer' or 'float', defaults to integer.
   */
  @Prop() format: JuiControlNumberFormat = JuiControlNumberFormat.int;

  /**
   * The number input format to use, can be 'integer' or 'float', defaults to integer.
   */
  @Prop({ mutable: true }) numstyle: JuiControlNumberStyle = JuiControlNumberStyle.decimal;

  /**
   * The precision number for decimal places when format is float. Defaults to 2.
   */
  @Prop() precision = 2;

  /**
   * The local currency (3 character code).
   */
  @Prop() currency: string = null;

  /**
   * The number input label.
   */
  @Prop() label: string;

  /**
   * The form name for the input control.
   */
  @Prop() name = '';

  /**
   * The number input placeholder. Defaults to the label.
   */
  @Prop() placeholder = '';

  /**
   * The number input min value. Defaults to null.
   */
  @Prop() min: number | null = null;

  /**
   * The number input max value. Defaults to null.
   */
  @Prop() max: null | number = null;

  /**
   * The number input step value. Defaults to 1.
   */
  @Prop() step = 1;

  /**
   * The number locale. Defaults to 'en-GB'.
   */
  @Prop() locale = 'en-GB';

  /**
   * Flag to control whether to show the steps.
   */
  @Prop() showsteps = true;

  /**
   * The event to trigger validation check
   */
  @Prop() validation: JuiValidationEvents = JuiValidationEvents.onLoad;

  /**
   * The number input value.
   */
  @Prop({ attribute: 'value', reflect: true, mutable: true })
  value: number | string | null;

  /**
   * The errors for the number input
   */
  @Prop() errors: boolean | { [key: string]: string };

  /**
   * The validation messages for the number input
   */
  @Prop() messages: string | { [key: string]: string };

  /**
   * Marking the number input field as readonly. Defaults to false
   */
  @Prop({ attribute: 'readonly', reflect: true })
  readonly = false;

  /**
   * Marking the number input field as disabled. Defaults to false
   */
  @Prop() disable: boolean;

  /**
   * Optionally autofocus the number input. Defaults to false
   */
  @Prop() autofocus = false;

  /**
   * Set the number input field to be clearable
   */
  @Prop() clearable = false;

  /**
   * Marking the number input field as required. Defaults to false
   */
  @Prop() required = false;

  /**
   * Optionally provide an icon to be displayed next to the number input field
   */
  @Prop() icon = '';

  /**
   * Set the number input field to be transitionable
   */
  @Prop() transitional = false;

  /**
   * Set the alignment of input field data (useful for table data) 'left', 'right'
   */
  @Prop() align: JuiControlAlign = JuiControlAlign.left;

  /**
   * Set the size of the number input. Options are 'md', 'lg' or 'sm'. Defaults to 'md'
   */
  @Prop() size: JuiControlSize = JuiControlSize.md;

  /**
   * Optional help string
   */
  @Prop() help: string = null;

  /**
   * Optional tooltip string
   */
  @Prop() tooltip: string = null;

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  /**
   * Output event emitted when change is fired
   */
  @Event() public juiChange: EventEmitter<number | string>;

  /**
   * Output event emitted when input is fired
   */
  @Event() public juiInput: EventEmitter<number | string>;

  /**
   * Output event emitted when blur is fired
   */
  @Event() public juiBlur: EventEmitter<number | string>;

  /**
   * Output event emitted when blur is fired
   */
  @Event() public juiFocus: EventEmitter<number | string>;

  @State()
  private tooltipLeft = false;

  @State()
  private tooltipLeftBottom = false;

  private get showClearIcon(): boolean {
    return this.clearable && !!this.value && this.value !== this.min;
  }

  private get showSteps(): boolean {
    return this.showsteps && this.step && this.step !== 1;
  }

  private get showMinMax(): boolean {
    return !!this.max && this.min !== null;
  }

  private get hasNumericValue(): boolean {
    if (this.value === '' || this.value === null || this.formattedDisplay === null || this.formattedDisplay === '') {
      return false;
    }
    return true;
  }

  private errorMessages = null;

  @State()
  private touched = false;

  @State()
  private focused = false;

  @State()
  private isClearing = false;

  @State()
  private inputValue: string;

  @State()
  private formattedDisplay: string = null;

  @State()
  private formattedMin: string = null;

  @State()
  private formattedMax: string = null;

  @State()
  private formattedStep: string = null;

  @State()
  private formattedZero: string = null;

  @State()
  formatInProgress = false;

  private i18nNumber;
  private i18nParser: NumberParser;
  private maximumDigits: number = null;
  private validKeys: string[] = [
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '.',
    ',',
    '-',
    'Escape',
    'Backspace',
    'Delete',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Tab',
    'Shift'
  ];

  private onInput(): void {
    this.juiInput.emit(this.value);
  }

  private onChange(): void {
    this.juiChange.emit(this.value);
  }

  private onBlur(): void {
    this.focused = false;
    // using blur event to make sure we format when leaving the control,
    // change won't fire if the underlying value is the same
    this.inputValue = (this.el.shadowRoot.querySelector('.jui-control__input') as HTMLInputElement).value;
    if (this.inputValue === '-') {
      // allow user to type negative number
      return;
    }
    if (this.numstyle !== JuiControlNumberStyle.percent) {
      this.inputValue = this.i18nParser.parseToDecimal(this.inputValue);
    }
    if (this.numstyle === JuiControlNumberStyle.percent) {
      this.inputValue = this.i18nParser.parseToPercent(this.inputValue, this.step);
    }

    // make sure component recognises the change to render,
    // even if actual value is the same (at max for example)
    this.formatInProgress = true;
    this.value = parseFloat(this.inputValue) >= this.max ? null : 99999;

    setTimeout(() => {
      this.value = this.i18nParser.parse(this.inputValue);
      this.value = this.snapToStep(this.value);
      this.formatInProgress = false;
      this.juiBlur.emit(this.value);
      this.juiInput.emit(this.value);
    }, 20);
  }

  private onFocus(): void {
    this.touched = true;
    this.focused = true;
    this.juiFocus.emit(this.value);
  }

  componentDidRender(): void {
    if (this.tooltip) {
      this.renderTooltips();
    }
  }

  componentWillLoad(): void {
    let numberFormatOptions: any = {
      style: this.numstyle,
      minimumFractionDigits: this.format === JuiControlNumberFormat.float ? this.precision : 0,
      maximumFractionDigits: this.format === JuiControlNumberFormat.float ? this.precision : 0
    };
    if (this.currency) {
      this.numstyle = JuiControlNumberStyle.currency;
      numberFormatOptions = { ...numberFormatOptions, currency: this.currency, style: this.numstyle };
    }
    this.i18nNumber = new Intl.NumberFormat(this.locale, numberFormatOptions);
    this.i18nParser = new NumberParser(this.locale);
    // used to restrict the length of the formatted display
    this.maximumDigits = this.max ? this.i18nNumber.format(this.max).length : null;
    this.setupLocaleFormats();
    if (this.validation === JuiValidationEvents.onLoad) {
      this.touched = true;
      this.renderError();
    }
  }

  componentDidLoad(): void {
    if (this.autofocus) {
      this.focusControl();
    }

    if (this.value || this.value === 0) {
      this.componentWillUpdate();
    }
  }

  componentWillUpdate(): void {
    this.renderError();
    if (this.value === null || this.value === '') {
      this.formattedDisplay = '';
      return;
    }
    if (this.min !== null) {
      this.value = this.parseValueToNumber(this.value) < this.min ? (this.value = this.min) : this.value;
    }

    if (typeof this.value !== 'string' && isNaN(this.value)) {
      this.value = this.min ?? 0;
    }
    this.formattedDisplay = this.i18nNumber.format(this.value);
  }

  private parseValueToNumber(val): number {
    let parsedValue = null;
    if (this.format === JuiControlNumberFormat.float) {
      parsedValue = parseFloat(val);
    } else {
      parsedValue = parseInt(val);
    }
    if (isNaN(parsedValue) || this.isClearing) {
      parsedValue = this.min !== null ? this.min : 0;
    }
    return parsedValue;
  }

  private parseValue(): void {
    const val = (this.el.shadowRoot.querySelector('.jui-control__input') as HTMLInputElement).value;
    this.value = this.i18nParser.parse(val);
    this.value = this.snapToStep(this.value);
    this.juiInput.emit(this.value);
    this.formattedDisplay = this.i18nNumber.format(this.value);
  }

  private clearInput = (evt: Event): void => {
    this.isClearing = true;
    this.parseValue();
    this.juiInput.emit(this.value);
    if (evt.type === 'click') {
      setTimeout((): void => {
        this.focusControl();
        this.isClearing = false;
      }, 30);
    } else {
      this.focusControl();
      this.isClearing = false;
    }
  };

  private stepUp(): void {
    if (this.readonly || this.disable) {
      return;
    }

    if (typeof this.value === 'string') {
      const parsed = parseFloat(this.value);
      this.value = !isNaN(parsed) ? parsed : 0;
    }

    if (!this.max) {
      this.value = this.value + this.step;
      this.formattedDisplay = this.i18nNumber.format(this.value);
      this.juiInput.emit(this.value);
      return;
    }

    if (this.value + this.step >= this.max) {
      this.value = this.max;
    } else {
      this.value = this.value + this.step;
    }
    this.formattedDisplay = this.i18nNumber.format(this.value);
    this.juiInput.emit(this.value);
  }

  private stepDown(): void {
    if (this.readonly || this.disable) {
      return;
    }
    if (typeof this.value === 'string') {
      const parsed = parseFloat(this.value);
      this.value = !isNaN(parsed) ? parsed : 0;
    }

    if (this.min === null) {
      this.value = this.value - this.step;
      this.formattedDisplay = this.i18nNumber.format(this.value);
      this.juiInput.emit(this.value);
      return;
    }

    if (this.value - this.step <= this.min) {
      this.value = this.min;
    } else {
      this.value = this.value - this.step;
    }
    this.formattedDisplay = this.i18nNumber.format(this.value);
    this.juiInput.emit(this.value);
  }

  private handleKeys(event) {
    if (event.key === 'Escape' && this.clearable) {
      this.clearInput(event);
    }
    if (event.key === 'ArrowUp') {
      this.stepUp();
    }
    if (event.key === 'ArrowDown') {
      this.stepDown();
    }
    if (!this.validKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  private snapToStep(val) {
    if (val === '') {
      return this.min;
    }
    if (this.min !== null && val <= this.min) {
      return this.min;
    }
    if (this.max && val >= this.max) {
      return this.max;
    }
    return Math.round(val / this.step) * this.step;
  }

  private focusControl(): void {
    const inputEl = this.el.shadowRoot.querySelector('.jui-control__input') as HTMLInputElement;
    inputEl.focus();
  }

  private setupLocaleFormats(): void {
    if (this.value) {
      this.formattedDisplay = this.i18nNumber.format(this.value);
    }
    if (this.min) {
      this.formattedMin = this.i18nNumber.format(this.min);
    }
    if (this.max) {
      this.formattedMax = this.i18nNumber.format(this.max);
    }
    if (this.step) {
      this.formattedStep = this.i18nNumber.format(this.step);
    }
    this.formattedZero = this.i18nNumber.format(0);
  }

  private getIconSize(): IconSize {
    switch (this.size) {
      case JuiControlSize.md:
        return IconSize.sm;
      case JuiControlSize.lg:
        return IconSize.lg;
      case JuiControlSize.sm:
        return IconSize.xs;
    }
  }

  private debounceInput(cb, delayMs) {
    let timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(() => {
        cb();
      }, delayMs);
    };
  }

  render() {
    const errorFragment = this.errorMessages ? this.errorMessages : null;
    const dataQaTagLabel = this.dataqastring ? `${this.dataqastring}_formFieldLabel` : null;
    const dataQaTagInput = this.dataqastring ? `${this.dataqastring}_formField` : null;

    const helpText = this.help && !this.transitional ? <div class="jui-control__help">{this.help}</div> : null;

    const tooltip = this.tooltip ? (
      <div
        class={{
          'jui-control__tooltip': true,
          [`jui-control__tooltip--input-${this.size}`]: true
        }}
      >
        <jui-icon
          class={{ 'jui-control__tooltip-icon': true }}
          name="help_outline"
          cursor="help"
          color={IconVariant.info}
          size={IconSize.xs}
          tabindex={-1}
        ></jui-icon>
        <span
          class={{
            'jui-control__tooltip-text': true,
            'jui-control__tooltip-text--left': this.tooltipLeft && !this.tooltipLeftBottom,
            'jui-control__tooltip-text--left-bottom': this.tooltipLeftBottom
          }}
        >
          {this.tooltip}
        </span>
      </div>
    ) : null;

    const icon = this.icon ? (
      <div
        class={{
          'jui-control__icon': true
        }}
      >
        <jui-icon name={this.icon} color={IconVariant.current} size={this.getIconSize()} isButton={false} />
      </div>
    ) : null;

    const clearableIcon = this.showClearIcon ? (
      <div
        class={{
          'jui-control__reset': true,
          [`jui-control__reset--${this.size}`]: true
        }}
      >
        <jui-icon
          name="clear"
          onClick={event => this.clearInput(event)}
          color={IconVariant.current}
          size={this.getIconSize()}
        />
      </div>
    ) : null;

    const stepper = (
      <div
        class={{
          'jui-control__stepper': true,
          [`jui-control__stepper--${this.size}`]: true
        }}
      >
        <div class="jui-control__step" data-direction="down">
          <jui-icon name="remove" onClick={() => this.stepDown()} color={IconVariant.high} size={this.getIconSize()} />
        </div>
        <div class="jui-control__step" data-direction="up">
          <jui-icon name="add" onClick={() => this.stepUp()} color={IconVariant.high} size={this.getIconSize()} />
        </div>
      </div>
    );

    const stepLabel = this.showSteps ? <span class="jui-control__label-step">+{this.formattedStep}</span> : null;

    const minMaxLabel = this.showMinMax ? (
      <span class="jui-control__label-min-max">
        <span class="jui-control__label--highlight">{this.formattedMin ?? this.formattedZero}</span> -{' '}
        <span class="jui-control__label--highlight">{this.formattedMax}</span>
      </span>
    ) : null;

    return (
      <Host data-qa={this.dataqastring || null}>
        <div
          class={{
            'jui-control': true,
            [`jui-control--${this.size}`]: true,
            [`jui-control--align-${this.align}`]: true,
            'jui-control--transitional': !!this.transitional,
            'jui-control--has-focus': !!this.isClearing
          }}
        >
          {this.label ? (
            <label
              {...(dataQaTagLabel && { 'data-qa': dataQaTagLabel })}
              class={{
              'jui-control__label': true,
              'jui-control__label--has-focus': !!this.focused || !!this.isClearing,
              'jui-control__label--has-value': this.hasNumericValue,
              'jui-control__label--has-icon': !!this.icon,
              'jui-control__label--disabled': !!this.disable
            }}
          >
            <div
              class={{
                'jui-control__label-text': true
              }}
            >
              <div>
                {this.required ? `${this.label} *` : this.label}
                {stepLabel}
              </div>
              <div>{minMaxLabel}</div>
            </div>
          </label>
        ) : null}
        {helpText}
        {tooltip}
        <div class="jui-control__input-wrapper">
          <input
            {...(dataQaTagInput && { 'data-qa': dataQaTagInput })}
            class={{
              'jui-control__input': true,
              'jui-control__input--clearable': !!this.clearable,
              'jui-control__input--has-icon': !!this.icon,
              'jui-control__input--invalid': (!!this.errors && this.touched) || (!!this.errors && !!this.focused),
              'jui-control__input--has-focus': !!this.isClearing || !!this.focused,
              'jui-control__input--formatting': !!this.formatInProgress
            }}
            onKeyDown={event => {
              this.handleKeys(event);
            }}
            name={this.name}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={this.maximumDigits}
            value={this.formattedDisplay}
            placeholder={this.placeholder.length ? this.placeholder : this.label}
            required={this.required}
            disabled={this.disable}
            readOnly={this.readonly}
            onInput={this.debounceInput(this.onInput.bind(this), 500)}
            onChange={this.onChange.bind(this)}
            onFocusout={this.onBlur.bind(this)}
            onFocusin={this.onFocus.bind(this)}
          />
          {icon}
          {clearableIcon}
          {stepper}
        </div>
          {errorFragment}
        </div>
      </Host>
    );
  }

  private renderTooltips(): void {
    this.tooltipLeft = renderTooltipOnLeft(this.el);
    if (this.tooltipLeft) {
      this.tooltipLeftBottom = renderTooltipOnBottom(this.el);
    }
  }

  private renderError() {
    this.errorMessages = renderFormError(this.errors, this.messages);
  }
}
